import { ComponentProps, ReactElement, ReactNode } from 'react';
import { compact, get } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import { BoxProps, Field, Text, VisuallyHidden } from '@chakra-ui/react';

import { FORM_FIELD_CLASS_NAME } from '../../constants/styles';
import { useEventHandler } from '../../hooks/useEventHandler';
import {
  FormFieldControlledComponent,
  FormFieldHooks,
  FormFieldUncontrolledComponent,
} from '../../types/fields';
import { FormLabel, FormLabelProps } from './FormLabel.component';

export type FormFieldProps<C, P> = P & {
  component: C;
  containerProps?: BoxProps;
  description?: string;
  helpText?: string;
  hooks?: FormFieldHooks;
  required?: boolean;
  readOnly?: boolean;
  label?: ReactNode;
  labelProps?: FormLabelProps;
  /**
   * Renders the label for assistive technology only; the field still takes its
   * accessible name from it.
   */
  hideLabel?: boolean;
  name: string;
  showMessageOnError?: boolean;
  hideValidationLabel?: boolean;
  optionalText?: string;

  // @TODO would prefer to do a setErrors on the useForm, but...
  manualError?: string;
};

export function FormField<
  C extends FormFieldControlledComponent | FormFieldUncontrolledComponent,
>({
  component,
  containerProps,
  description,
  helpText,
  hooks,
  readOnly = false,
  required = false,
  name,
  label,
  labelProps,
  hideLabel = false,
  showMessageOnError = true,
  manualError = '',
  hideValidationLabel,
  optionalText,
  ...props
}: FormFieldProps<
  C,
  Omit<ComponentProps<C>, 'onBlur' | 'onChange' | 'value'>
>): ReactElement {
  const {
    control,
    formState: { errors },
    register,
  } = useFormContext();

  const error = get(errors, name)?.message || manualError;

  const hiddenContainerClass =
    props?.type === 'hidden' ? { display: 'none' } : {};
  const sharedProps = {
    hooks,
    invalid: error ? 'invalid' : undefined,
    readOnly,
    required,
  };

  const renderContolledComponent = useEventHandler(
    ({ field: { ref: _ref, ...field } }) => {
      const Component = component as FormFieldControlledComponent;
      return <Component _ref={_ref} {...sharedProps} {...field} {...props} />;
    },
  );

  const renderLabel = (): ReactNode => {
    // The required/optional indicator is an empty span the field recipe gives
    // a CSS asterisk, so a hidden label omits it rather than reading it into
    // the field's accessible name.
    const formLabel = (
      <FormLabel
        helpText={helpText}
        hideValidationLabel={hideLabel || hideValidationLabel}
        htmlFor={name}
        optionalText={optionalText}
        readOnly={readOnly}
        required={required}
        {...labelProps}
      >
        {label}
      </FormLabel>
    );

    if (!hideLabel) return formLabel;

    return <VisuallyHidden asChild>{formLabel}</VisuallyHidden>;
  };

  const renderComponent = (): ReactNode => {
    if (component.controlled) {
      return (
        <Controller
          control={control}
          name={name}
          render={renderContolledComponent}
        />
      );
    }

    const Component = component as FormFieldUncontrolledComponent;
    const { ref: _ref, ...field } = register(name);
    return <Component _ref={_ref} {...sharedProps} {...field} {...props} />;
  };

  // Chakra's `Field.Root` sets `role="group"`, and an ancestor that names
  // itself from its contents skips an unnamed group, so a single label and
  // control is marked presentational instead.
  return (
    <Field.Root
      {...{ ...hiddenContainerClass, ...containerProps }}
      className={compact([FORM_FIELD_CLASS_NAME, component.displayName]).join(
        ' ',
      )}
      invalid={!!error}
      role="presentation"
    >
      {label && renderLabel()}

      {renderComponent()}

      {description && (
        <Text color="fg.muted" fontSize="sm" mt={2}>
          {description}
        </Text>
      )}

      {error && showMessageOnError && (
        <Field.ErrorText mt={2}>{`${error}`}</Field.ErrorText>
      )}
    </Field.Root>
  );
}
